
import React, { useState } from 'react';
import { Subscription, SubscriptionStatus, PlanTier } from '../types';

interface SubscriptionTableProps {
  subscriptions: Subscription[];
  onSelect?: (subscription: Subscription) => void;
}

const CopyIcon: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button 
      onClick={handleCopy}
      className={`ml-1.5 p-1 rounded-md transition-all ${copied ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/30' : 'text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 opacity-0 group-hover/id:opacity-100'}`}
    >
      {copied ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>
      )}
    </button>
  );
};

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({ subscriptions, onSelect }) => {
  const safeSubscriptions = Array.isArray(subscriptions) ? subscriptions : [];

  const formatCurrency = (value: unknown) => {
    const amount = Number(value || 0);
    return "$" + (Number.isFinite(amount) ? amount.toLocaleString() : "0");
  };

  const getSubscriptionData = (subscription: Subscription) => {
    return subscription as Subscription & {
      amount?: number | string;
      price?: number | string;
      name?: string;
      email?: string;
      plan?: string | number | { name?: string; tier?: string; title?: string; id?: string | number };
      planId?: string | number;
      plan_id?: string | number;
      billing_cycle?: string;
      current_end?: string | number;
      gateway_ref?: string;
      subscriptionId?: string;
    };
  };

  const titleize = (value: unknown, fallback: string) => {
    const text = String(value ?? "").trim();
    if (!text) return fallback;
    return text
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const getSubscriptionMrr = (subscription: Subscription) => {
    const subscriptionData = getSubscriptionData(subscription);
    if (subscriptionData.mrr != null) return subscriptionData.mrr;

    const rawAmount = Number(subscriptionData.amount ?? subscriptionData.price ?? 0);
    if (!Number.isFinite(rawAmount)) return 0;

    return String(getBillingCycle(subscription)).toLowerCase() === "annually" ? Math.round(rawAmount / 12) : rawAmount;
  };

  const getCustomerName = (subscription: Subscription) => {
    const subscriptionData = getSubscriptionData(subscription);
    return subscriptionData.customerName || subscriptionData.name || subscriptionData.email || subscriptionData.customerId || "Unknown Customer";
  };

  const getPlanName = (subscription: Subscription) => {
    const subscriptionData = getSubscriptionData(subscription);
    const plan = subscriptionData.plan;
    if (plan != null && typeof plan === "object") {
      const planData = plan as { name?: string; tier?: string; title?: string; id?: string | number };
      return planData.name || planData.tier || planData.title || String(planData.id || "Custom Plan");
    }
    return titleize(plan ?? subscriptionData.planId ?? subscriptionData.plan_id, "Custom Plan");
  };

  const getBillingCycle = (subscription: Subscription) => {
    const subscriptionData = getSubscriptionData(subscription);
    return titleize(subscriptionData.billingCycle || subscriptionData.billing_cycle, "Monthly");
  };

  const getNextBillingDate = (subscription: Subscription) => {
    const subscriptionData = getSubscriptionData(subscription);
    const rawDate = subscriptionData.nextBillingDate || subscriptionData.current_end;
    if (!rawDate) return "-";
    const date = typeof rawDate === "number" ? new Date(rawDate * 1000) : new Date(rawDate);
    return Number.isNaN(date.getTime()) ? String(rawDate) : date.toLocaleDateString();
  };

  const getGatewayRef = (subscription: Subscription) => {
    const subscriptionData = getSubscriptionData(subscription);
    return subscriptionData.gatewayRef || subscriptionData.gateway_ref || subscriptionData.subscriptionId || subscriptionData.id;
  };
  const getStatusStyle = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE: return 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800';
      case SubscriptionStatus.PAST_DUE: return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      case SubscriptionStatus.PAUSED: return 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      case SubscriptionStatus.CANCELED: return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
      default: return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
    }
  };

  const getPlanStyle = (plan: PlanTier) => {
    switch (plan) {
      case PlanTier.ENTERPRISE: return 'text-purple-700 border-purple-200 bg-purple-50 dark:text-purple-300 dark:bg-purple-900/20 dark:border-purple-800';
      case PlanTier.PRO: return 'text-indigo-700 border-indigo-200 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/20 dark:border-indigo-800';
      default: return 'text-slate-700 border-slate-200 bg-slate-50 dark:text-slate-300 dark:bg-slate-800 dark:border-slate-700';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Internal ID</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Gateway Ref</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Plan</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Cycle</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">MRR</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Next Billing</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {safeSubscriptions.map((sub) => (
              <tr 
                key={sub.id} 
                className={`group hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${onSelect ? 'cursor-pointer' : ''}`}
                onClick={() => onSelect && onSelect(sub)}
              >
                <td className="px-8 py-5">
                  <div className="flex items-center group/id">
                    <span className="font-mono text-[10px] font-black text-slate-400 uppercase">{sub.id}</span>
                    <CopyIcon text={sub.id} />
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex items-center group/id">
                    <span className="font-mono text-[9px] font-bold text-slate-400 truncate max-w-[120px]">{getGatewayRef(sub)}</span>
                    <CopyIcon text={getGatewayRef(sub)} />
                  </div>
                </td>
                <td className="px-8 py-5">
                  <div className="flex flex-col">
                    <span className="text-sm font-black text-slate-900 dark:text-white">{getCustomerName(sub)}</span>
                  </div>
                </td>
                <td className="px-8 py-5"><span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-tight border ${getPlanStyle(getPlanName(sub) as PlanTier)}`}>{getPlanName(sub)}</span></td>
                <td className="px-8 py-5"><span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusStyle(sub.status)}`}>{sub.status}</span></td>
                <td className="px-8 py-5 text-xs font-bold text-slate-600 dark:text-slate-300">{getBillingCycle(sub)}</td>
                <td className="px-8 py-5 text-sm font-black text-slate-900 dark:text-white">{formatCurrency(getSubscriptionMrr(sub))}</td>
                <td className="px-8 py-5 text-xs text-slate-500 dark:text-slate-400 font-bold">{getNextBillingDate(sub)}</td>
                <td className="px-8 py-5 text-right">
                   <button 
                    onClick={(e) => { e.stopPropagation(); onSelect && onSelect(sub); }}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                   >
                     <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></svg>
                   </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubscriptionTable;
